import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscriptionsAPI } from '../../api/subscriptions';
import { formatCurrency } from '../../utils/formatters';
import { NotificationContext } from '../../context/NotificationContext';
import Button from '../common/Button';
import Modal from '../common/Modal';
import SubscriptionPlanForm from './SubscriptionPlanForm';
import './SubscriptionManagement.css';

// ─── Constants ────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'all',      label: 'All Plans',    icon: '📋', filter: null },
  { id: 'active',   label: 'Active',       icon: '✅', filter: 'active' },
  { id: 'inactive', label: 'Inactive',     icon: '⏸️', filter: 'inactive' },
];

// ─── Inline components ────────────────────────────────────────────────────────
const SummaryCard = ({ icon, label, value, sub, accent }) => (
  <div className="ps-summary-card" style={{ borderTopColor: accent }}>
    <div className="ps-summary-icon">{icon}</div>
    <div>
      <div className="ps-summary-value" style={{ color: accent }}>{value}</div>
      <div className="ps-summary-label">{label}</div>
      {sub && <div className="ps-summary-sub">{sub}</div>}
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const PlanManagement = () => {
  const navigate = useNavigate();
  const { showNotification } = useContext(NotificationContext);

  // Tab + list state
  const [activeTab, setActiveTab]   = useState('all');
  const [plans, setPlans]           = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading]       = useState(false);

  // Summary
  const [summary, setSummary]       = useState(null);

  // Detail modal
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading]     = useState(false);

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editTarget, setEditTarget]       = useState(null);
  const [editLoading, setEditLoading]     = useState(false);

  // Deactivate modal
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivateTarget, setDeactivateTarget]       = useState(null);
  const [deactivateLoading, setDeactivateLoading]     = useState(false);

  // ── Load summary ────────────────────────────────────────────────────────────
  const loadSummary = useCallback(async () => {
    try {
      const res = await subscriptionsAPI.getPlansSummary();
      setSummary(res.data);
    } catch { /* non-critical */ }
  }, []);

  // ── Load plans ──────────────────────────────────────────────────────────────
  const loadPlans = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      const tabFilter = TABS.find(t => t.id === activeTab)?.filter;
      if (tabFilter === 'active') params.isActive = 'true';
      else if (tabFilter === 'inactive') params.isActive = 'false';

      const res = await subscriptionsAPI.getPlans(params);
      setPlans(res.data);
      setTotalCount(res.data.length);
    } catch {
      showNotification('Failed to load plans', 'error');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => { loadSummary(); }, []);
  useEffect(() => { loadPlans(); }, [activeTab]);

  // ── Create plan ─────────────────────────────────────────────────────────────
  const handleCreate = async (data) => {
    setCreateLoading(true);
    try {
      await subscriptionsAPI.createPlan(data);
      showNotification('Plan created successfully', 'success');
      setShowCreateModal(false);
      loadPlans();
      loadSummary();
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to create plan', 'error');
    } finally {
      setCreateLoading(false);
    }
  };

  // ── Edit plan ───────────────────────────────────────────────────────────────
  const openEditModal = (plan, e) => {
    e?.stopPropagation();
    setEditTarget(plan);
    setShowEditModal(true);
  };

  const handleEdit = async (data) => {
    setEditLoading(true);
    try {
      await subscriptionsAPI.updatePlan(editTarget._id, data);
      showNotification('Plan updated successfully', 'success');
      setShowEditModal(false);
      setEditTarget(null);
      loadPlans();
      loadSummary();
      if (selectedPlan?._id === editTarget._id) {
        const res = await subscriptionsAPI.getPlanById(editTarget._id);
        setSelectedPlan(res.data);
      }
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to update plan', 'error');
    } finally {
      setEditLoading(false);
    }
  };

  // ── Deactivate plan ─────────────────────────────────────────────────────────
  const handleDeactivate = async () => {
    setDeactivateLoading(true);
    try {
      await subscriptionsAPI.updatePlan(deactivateTarget._id, { isActive: false });
      showNotification('Plan deactivated', 'success');
      setShowDeactivateModal(false);
      setDeactivateTarget(null);
      if (selectedPlan?._id === deactivateTarget._id) setSelectedPlan(null);
      loadPlans();
      loadSummary();
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to deactivate plan', 'error');
    } finally {
      setDeactivateLoading(false);
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const tabCount = (filterId) => {
    if (!summary || !filterId) return null;
    if (filterId === 'active') return summary.activeCount;
    if (filterId === 'inactive') return summary.inactiveCount;
    return null;
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="ps-root">

      {/* ── Header ── */}
      <div className="ps-header">
        <div>
          <h1 className="ps-title">Subscription Plans</h1>
          <p className="ps-subtitle">Manage subscription plans and pricing</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="secondary" onClick={() => navigate('/subscriptions')}>
            ← Back to Subscriptions
          </Button>
          <Button variant="primary" onClick={() => setShowCreateModal(true)}>
            + New Plan
          </Button>
        </div>
      </div>

      {/* ── Summary Cards ── */}
      {summary && (
        <div className="ps-summary-row">
          <SummaryCard
            icon="📋" label="Total Plans" accent="#3b82f6"
            value={summary.totalCount ?? 0}
            sub={`${summary.activeCount ?? 0} active`}
          />
          <SummaryCard
            icon="👥" label="Active Subscribers" accent="#10b981"
            value={summary.totalActiveSubscriptions ?? 0}
            sub="across all plans"
          />
          <SummaryCard
            icon="💰" label="Monthly Revenue" accent="#8b5cf6"
            value={formatCurrency(summary.totalMonthlyRevenue ?? 0)}
            sub="from active plans"
          />
          <SummaryCard
            icon="📈" label="Avg. Plan Price" accent="#f59e0b"
            value={formatCurrency(summary.avgPrice ?? 0)}
            sub="across all plans"
          />
          <SummaryCard
            icon="⏱️" label="Avg. Duration" accent="#06b6d4"
            value={`${summary.avgDuration ?? 0} days`}
            sub="per plan"
          />
        </div>
      )}

      {/* ── Tabs ── */}
      <div className="ps-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`ps-tab${activeTab === tab.id ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="ps-tab-icon">{tab.icon}</span>
            {tab.label}
            {tab.filter && tabCount(tab.filter) != null && (
              <span className="om-tab-count">{tabCount(tab.filter)}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Content ── */}
      <div className="ps-content">
        <div className="ps-tab-pane">

          {/* Table meta */}
          <div className="ps-table-meta">
            <span className="ps-count">{totalCount} plan{totalCount !== 1 ? 's' : ''}</span>
          </div>

          {/* Table */}
          {loading ? (
            <div className="ps-loading">Loading plans…</div>
          ) : (
            <div className="ps-table-wrap">
              <table className="ps-table">
                <thead>
                  <tr>
                    <th>Plan Name</th>
                    <th>Price</th>
                    <th>Duration</th>
                    <th>Discount</th>
                    <th>Benefits</th>
                    <th>Subscribers</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {plans.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="ps-empty">
                        <div style={{ fontSize: 36, marginBottom: 8 }}>📋</div>
                        No plans found
                      </td>
                    </tr>
                  ) : plans.map(plan => (
                    <tr key={plan._id} className="ps-row" onClick={() => setSelectedPlan(plan)}>
                      {/* Plan Name */}
                      <td>
                        <div style={{ fontWeight: 700, color: '#6366f1', fontSize: 14 }}>{plan.name}</div>
                        {plan.description && (
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{plan.description}</div>
                        )}
                      </td>

                      {/* Price */}
                      <td style={{ fontWeight: 800, color: '#0f172a', fontSize: 15 }}>
                        {formatCurrency(plan.price)}
                      </td>

                      {/* Duration */}
                      <td style={{ color: '#64748b' }}>{plan.duration} days</td>

                      {/* Discount */}
                      <td>
                        {plan.benefits?.discountPercentage > 0 ? (
                          <span style={{
                            background: '#d1fae5', color: '#065f46',
                            padding: '4px 10px', borderRadius: 12,
                            fontSize: 12, fontWeight: 700
                          }}>
                            {plan.benefits.discountPercentage}% OFF
                          </span>
                        ) : (
                          <span style={{ color: '#d1d5db' }}>—</span>
                        )}
                      </td>

                      {/* Benefits */}
                      <td>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, fontSize: 11 }}>
                          {plan.benefits?.bundleClothes > 0 && (
                            <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>
                              👔 {plan.benefits.bundleClothes}
                            </span>
                          )}
                          {plan.benefits?.freeItems > 0 && (
                            <span style={{ background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>
                              🎁 {plan.benefits.freeItems}
                            </span>
                          )}
                          {plan.benefits?.expressDeliveryFree && (
                            <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>
                              🚀
                            </span>
                          )}
                          {plan.benefits?.priorityService && (
                            <span style={{ background: '#ede9fe', color: '#5b21b6', padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>
                              ⭐
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Subscribers */}
                      <td style={{ fontWeight: 600, color: '#374151' }}>
                        {plan.activeSubscribers ?? 0}
                      </td>

                      {/* Status */}
                      <td>
                        <span className="om-badge" style={plan.isActive
                          ? { background: '#d1fae5', color: '#065f46' }
                          : { background: '#fee2e2', color: '#991b1b' }
                        }>
                          {plan.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>

                      {/* Actions */}
                      <td onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button
                            className="om-action-btn"
                            onClick={e => openEditModal(plan, e)}
                            title="Edit plan"
                          >
                            ✏️
                          </button>
                          {plan.isActive && (
                            <button
                              className="om-action-btn danger"
                              onClick={e => { e.stopPropagation(); setDeactivateTarget(plan); setShowDeactivateModal(true); }}
                              title="Deactivate"
                            >
                              ⏸️
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          PLAN DETAIL MODAL
          ══════════════════════════════════════════════════════════════════════ */}
      {selectedPlan && (
        <Modal
          isOpen={!!selectedPlan}
          onClose={() => setSelectedPlan(null)}
          title={selectedPlan.name}
          size="large"
        >
          <PlanDetailContent
            plan={selectedPlan}
            onEdit={openEditModal}
            formatCurrency={formatCurrency}
          />
        </Modal>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          CREATE MODAL
          ══════════════════════════════════════════════════════════════════════ */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Plan"
      >
        <SubscriptionPlanForm
          onSubmit={handleCreate}
          onCancel={() => setShowCreateModal(false)}
        />
      </Modal>

      {/* ══════════════════════════════════════════════════════════════════════
          EDIT MODAL
          ══════════════════════════════════════════════════════════════════════ */}
      <Modal
        isOpen={showEditModal}
        onClose={() => { setShowEditModal(false); setEditTarget(null); }}
        title="Edit Plan"
      >
        {editTarget && (
          <SubscriptionPlanForm
            plan={editTarget}
            onSubmit={handleEdit}
            onCancel={() => { setShowEditModal(false); setEditTarget(null); }}
          />
        )}
      </Modal>

      {/* ══════════════════════════════════════════════════════════════════════
          DEACTIVATE MODAL
          ══════════════════════════════════════════════════════════════════════ */}
      <Modal
        isOpen={showDeactivateModal}
        onClose={() => { setShowDeactivateModal(false); setDeactivateTarget(null); }}
        title="Deactivate Plan"
      >
        {deactivateTarget && (
          <div className="ps-modal-form">
            <div className="om-delete-warning">
              <span style={{ fontSize: 32 }}>⚠️</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', marginBottom: 4 }}>
                  Deactivate "{deactivateTarget.name}" plan?
                </div>
                <div style={{ fontSize: 13, color: '#64748b' }}>
                  This plan will no longer be available for new subscriptions. Existing subscriptions will remain active.
                </div>
              </div>
            </div>
            <div className="ps-modal-actions">
              <Button variant="secondary" onClick={() => { setShowDeactivateModal(false); setDeactivateTarget(null); }}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleDeactivate} disabled={deactivateLoading}>
                {deactivateLoading ? 'Deactivating…' : 'Deactivate Plan'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

// ─── Plan Detail Content ──────────────────────────────────────────────────────
const PlanDetailContent = ({ plan, onEdit, formatCurrency }) => {
  const benefits = plan.benefits || {};

  return (
    <div className="pm-detail">

      {/* Top bar */}
      <div className="om-detail-top-bar">
        <div className="om-detail-badges">
          <span className="om-badge" style={plan.isActive
            ? { background: '#d1fae5', color: '#065f46' }
            : { background: '#fee2e2', color: '#991b1b' }
          }>
            {plan.isActive ? '✅ Active' : '⏸️ Inactive'}
          </span>
          {benefits.discountPercentage > 0 && (
            <span className="om-badge" style={{ background: '#d1fae5', color: '#065f46' }}>
              💸 {benefits.discountPercentage}% OFF
            </span>
          )}
        </div>
        <button className="ps-settle-btn-lg" onClick={(e) => onEdit(plan, e)}>Edit Plan</button>
      </div>

      {/* 2-column grid */}
      <div className="pm-detail-grid">

        {/* Plan Info */}
        <div className="pm-plan-card">
          <div className="pm-plan-name">{plan.name}</div>
          {plan.description && <div className="pm-plan-desc">{plan.description}</div>}
          <div className="pm-plan-price">{formatCurrency(plan.price)}</div>
          <div className="pm-plan-duration">{plan.duration} days</div>
          
          <div className="pm-plan-divider" />
          
          <div className="pm-benefits-list">
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8, opacity: 0.9 }}>
              Plan Benefits
            </div>
            {benefits.discountPercentage > 0 && (
              <div className="pm-benefit-item">
                <span className="pm-benefit-item-icon">💸</span>
                <span>{benefits.discountPercentage}% discount on all orders</span>
              </div>
            )}
            {benefits.bundleClothes > 0 && (
              <div className="pm-benefit-item">
                <span className="pm-benefit-item-icon">👔</span>
                <span>{benefits.bundleClothes} items included in bundle</span>
              </div>
            )}
            {benefits.freeItems > 0 && (
              <div className="pm-benefit-item">
                <span className="pm-benefit-item-icon">🎁</span>
                <span>{benefits.freeItems} free items per month</span>
              </div>
            )}
            {benefits.expressDeliveryFree && (
              <div className="pm-benefit-item">
                <span className="pm-benefit-item-icon">🚀</span>
                <span>Free express delivery</span>
              </div>
            )}
            {benefits.priorityService && (
              <div className="pm-benefit-item">
                <span className="pm-benefit-item-icon">⭐</span>
                <span>Priority service</span>
              </div>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="om-detail-section">
          <div className="om-section-title">Plan Statistics</div>
          <div className="pm-stats-grid">
            <div className="pm-stat-box">
              <div className="pm-stat-value">{plan.activeSubscribers ?? 0}</div>
              <div className="pm-stat-label">Active Subscribers</div>
            </div>
            <div className="pm-stat-box">
              <div className="pm-stat-value">{formatCurrency((plan.activeSubscribers ?? 0) * plan.price)}</div>
              <div className="pm-stat-label">Monthly Revenue</div>
            </div>
          </div>
          
          {/* Value proposition */}
          {benefits.discountPercentage > 0 && (
            <div style={{
              background: '#f0fdf4', border: '1px solid #bbf7d0',
              borderRadius: 10, padding: '12px 14px', marginTop: 12
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#065f46', textTransform: 'uppercase', marginBottom: 4 }}>
                💡 Value Proposition
              </div>
              <div style={{ fontSize: 12, color: '#166534', lineHeight: 1.5 }}>
                If customer spends <strong>KD 100.000/month</strong>, they save{' '}
                <strong>KD {(100 * benefits.discountPercentage / 100).toFixed(3)}</strong> monthly.
                Plan pays for itself in{' '}
                <strong>{Math.ceil(plan.price / (100 * benefits.discountPercentage / 100))} month{Math.ceil(plan.price / (100 * benefits.discountPercentage / 100)) !== 1 ? 's' : ''}</strong>!
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlanManagement;
